import { useRef } from "react"
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react"
import { Box, TextField } from "@mui/material"
import type { BoxProps } from "@mui/material"

const NON_DIGITS = /\D/g

export interface OTPInputProps extends Omit<BoxProps, "onChange"> {
  /** The code so far. Shorter than `length` leaves the trailing cells empty. */
  value: string
  length?: number
  autoFocus?: boolean
  onChange: (value: string) => void
  /** Fired once every cell is filled, with the whole `length`-digit code. */
  onComplete?: (value: string) => void
}

/**
 * A one-time-password field: `length` single-digit cells that behave like one input.
 *
 * The cells are `type="text"` with `inputMode="numeric"` rather than `type="number"`:
 * a number input still gets the numeric keypad on mobile, but the selection API does
 * not apply to it, so `selectionStart` reads `null` and `select()` is a no-op. Every
 * caret-aware behaviour below — arrows, Home/End, stepping back on backspace — would
 * silently do nothing.
 */
const OTPInput = ({
  value,
  length = 6,
  autoFocus = false,
  onChange,
  onComplete,
  ...boxProps
}: OTPInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  const characters = Array.from({ length }, (_, index) => value[index] ?? "")

  const focusCell = (index: number) => {
    const input = inputsRef.current[index]
    if (!input) return
    input.focus()
    input.select()
  }

  // The cells render `value[index]`, so a gap collapses: clearing cell 2 of "123"
  // yields "13" and the 3 slides left. Callers hold the code as a plain string, so
  // that is the contract — the focus helpers below account for the shift.
  const emit = (nextCharacters: string[]) => {
    const next = nextCharacters.join("")
    onChange(next)
    if (next.length === length) onComplete?.(next)
    return next
  }

  const replaceAt = (index: number, character: string) =>
    characters.map((current, position) => (position === index ? character : current))

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const typed = input.value.replace(NON_DIGITS, "").charAt(0)

    if (!typed && input.value) {
      // React restores the value on its own, but that write moves the caret to the end.
      // Setting it here first makes React's restore a no-op, so the `select()` holds.
      input.value = characters[index]
      input.select()
      return
    }

    const next = emit(replaceAt(index, typed))

    if (!typed) {
      // The cell was emptied. Anything to its right has shifted one place left.
      if (next.length <= index) focusCell(index - 1)
      return
    }
    // The digit lands at the end of the string when the cells before it are empty.
    focusCell(Math.min(index, next.length - 1) + 1)
  }

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLDivElement>) => {
    const input = event.target as HTMLInputElement

    // Re-typing the digit already in the cell leaves the value untouched, so React
    // never fires a change event. Advance from here instead.
    if (input.value === event.key) {
      event.preventDefault()
      focusCell(index + 1)
      return
    }

    switch (event.key) {
      case "Backspace":
        if (!input.value) {
          event.preventDefault()
          focusCell(index - 1)
        } else if (input.selectionStart === 0 && input.selectionEnd === 0) {
          // Caret before the digit and nothing selected: the browser would delete
          // backwards into the previous cell, which it cannot reach.
          event.preventDefault()
          const next = emit(replaceAt(index, ""))
          if (next.length <= index) focusCell(index - 1)
        }
        break
      case "ArrowLeft":
        event.preventDefault()
        focusCell(index - 1)
        break
      case "ArrowRight":
        event.preventDefault()
        focusCell(index + 1)
        break
      case "Home":
        event.preventDefault()
        focusCell(0)
        break
      case "End":
        event.preventDefault()
        focusCell(length - 1)
        break
    }
  }

  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData("text/plain").replace(NON_DIGITS, "")
    if (!pasted) return

    // Fill from the first empty cell, or from the one being pasted into if it comes first.
    const firstEmpty = characters.indexOf("")
    const start = firstEmpty === -1 ? index : Math.min(firstEmpty, index)
    const next = characters.map((current, position) =>
      position < start ? current : (pasted[position - start] ?? ""),
    )

    const code = emit(next)
    focusCell(Math.min(code.length, length - 1))
  }

  return (
    <Box display="flex" gap="20px" alignItems="center" {...boxProps}>
      {characters.map((character, index) => (
        <TextField
          key={index}
          value={character}
          autoFocus={autoFocus && index === 0}
          autoComplete="one-time-code"
          inputRef={(element: HTMLInputElement | null) => {
            inputsRef.current[index] = element
          }}
          // `inputMode` is not a TextField prop: passed at the top level it would land
          // on the wrapper div and the mobile keypad would stay alphanumeric.
          inputProps={{ maxLength: 1, inputMode: "numeric", pattern: "[0-9]*" }}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste(index)}
          onFocus={(event) => event.target.select()}
          // The theme gives every `MuiFormControl` — which is what a TextField root
          // is — a `marginBottom` of 1rem. A cell is not a form field on its own, so
          // it would just push whatever follows the code away by 16px.
          sx={{ margin: 0, input: { textAlign: "center" } }}
        />
      ))}
    </Box>
  )
}

export default OTPInput
